import { Component } from './component.class';
import { getFuelSkuSuffix } from '../../helpers/fuel-choice';
import { ComponentDataIn, ComponentsData } from './types';

import { COMPONENT_APPLICATION_STAND_ALONE, COMPONENT_CATEGORIES_BOUND_TO_GROUND, COMPONENT_EAR_PADDING, COMPONENT_PLACEMENT_BOTTOM_ONLY, COMPONENT_PLACEMENT_FRONT_AND_TOP, COMPONENT_PLACEMENT_FRONT_ONLY, COMPONENT_PLACEMENT_TOP_ONLY } from '../constants';

import { ComponentItemData } from '../data/component-item-data.class';
import { ComponentOptions } from '../types';

export class Components extends Array<Component> {
	#userEmail: string;

	constructor (options: ComponentOptions<ComponentsData>) {
		super();
		this.#userEmail = options?.userEmail;

		if (options?.dataIn?.length > 0) {
			options.dataIn.forEach((n) => this.push(new Component({ ...options, dataIn: n })));
		} else {
			this.load();
		}
	}

	/**
	 *
	 * @param newComponentData ComponentData of component to add
	 * @returns unique identified of added component
	 */
	public add (newComponentData: ComponentDataIn): string {
		const newRecord = new Component({ userEmail: this.#userEmail, dataIn: newComponentData });
		this.push(newRecord);
		return newRecord.id;
	}

	/**
	 *
	 * @param sku vendors product id to lok up
	 * @returns Component
	 */
	public findSku (sku: string): Component {
		const skuItem = this.find((record: Component) => record.sku === sku) as Component || null;
		return skuItem;
	}

	/**
	 *
	 * @param id Unique identifier of component to find
	 * @returns Component
	 */
	public findId (id: string): Component {
		return this.find((record: Component) => record.id === id) || null;
	}

	/**
	 * Finds the catalog row for one fuel of an appliance family.
	 * @param fuelFamily key shared by every fuel row of the same appliance
	 * @param fuelType which fuel row to return ('LP' or 'NG')
	 * @returns Component, or null when the family has no row for that fuel
	 */
	public findFuelVariant (fuelFamily: string, fuelType: string): Component {
		return this.find((record: Component) => record.fuelFamily === fuelFamily && record.fuelType === fuelType) || null;
	}

	/**
	 * Maps a component to the row for the given fuel. A fuel appliance is stored as one row per
	 * fuel sharing a `fuelFamily`, so ANY variant's row — or a pre-split base row carrying the
	 * family key — resolves to the right one. This is the single place fuel selection happens:
	 * the appliance picker, deserializing a saved design and switching a kitchen's fuel all
	 * route through it. A component with no family (or a family missing that fuel) is returned
	 * untouched, so an un-split catalog behaves exactly as before.
	 * @param component the component to map
	 * @param fuelchoice the effective fuel (item override, else the kitchen's)
	 * @returns the variant Component for that fuel, or the original component
	 */
	public resolveFuelVariant (component: Component, fuelchoice: string | null): Component {
		if (!component?.fuelFamily) return component;
		return this.findFuelVariant(component.fuelFamily, getFuelSkuSuffix(fuelchoice)) ?? component;
	}

	// loads json, returns record count loaded
	// TODO: loaders should be an injectable interface to allow for external sources
	public load (): number {
		const componentItemData = ComponentItemData.getData();

		// purge before load
		if (this.length > 0) this.splice(0, this.length);

		// load data
		let loadCounter = 0;
		for (const sku in componentItemData) {
			if (componentItemData[sku]) {
				loadCounter++;
				this.add(componentItemData[sku]);
			}
		}

		return loadCounter;
	}

	/**
	 *
	 * @returns serialized data representing class state for persistence
	 */
	public serialize ():ComponentsData {
		return this.map((record: Component) => record.serialize());
	}

	/* Constants */
	static EAR_PADDING = COMPONENT_EAR_PADDING;

	// static CATEGORY_DROP_IN = COMPONENT_CATEGORY_DROP_IN;
	// static CATEGORY_FRIDGE = COMPONENT_CATEGORY_FRIDGE;
	// static CATEGORY_VENT = COMPONENT_CATEGORY_VENT;

	static CATEGORIES_BOUND_TO_GROUND	= COMPONENT_CATEGORIES_BOUND_TO_GROUND;

	static PLACEMENT_FRONT_ONLY = COMPONENT_PLACEMENT_FRONT_ONLY;
	static PLACEMENT_TOP_ONLY = COMPONENT_PLACEMENT_TOP_ONLY;
	static PLACEMENT_FRONT_AND_TOP = COMPONENT_PLACEMENT_FRONT_AND_TOP;
	static PLACEMENT_BOTTOM_ONLY = COMPONENT_PLACEMENT_BOTTOM_ONLY;
	static readonly placement = [
		{ key: COMPONENT_PLACEMENT_FRONT_ONLY, displayName: 'Front Only' },
		{ key: COMPONENT_PLACEMENT_TOP_ONLY, displayName: 'Top Only' },
		{ key: COMPONENT_PLACEMENT_FRONT_AND_TOP, displayName: 'Front and Top' },
		{ key: COMPONENT_PLACEMENT_BOTTOM_ONLY, displayName: 'Bottom Only' }
	];

	static APPLICATION_STAND_ALONE = COMPONENT_APPLICATION_STAND_ALONE;
}
